import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Card, Modal, Form, Space, App, Button } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import PageHeader from './PageHeader';
import DataTable from './DataTable';
import RenderForm, { uploadLinkFieldName } from './RenderForm';
import TableAction from './TableAction';
import { uploadImageCDN } from '../../utils/utility';
import { uploadImageToServer } from '../../services/cms/contentManagementService';
import request from '../../config/axiosConfig';

dayjs.extend(customParseFormat);

// Tránh tạo reference mới mỗi render gây infinite loop trong useEffect
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

// Chỉ những queryKey có trong bảng này mới gọi được /miniapp/batch-action-content; các key
// khác backend không nhận ra model nên trả 400 (xem Cms/index.js:307) -> coi như không có
// thao tác hàng loạt và ẩn luôn ô tích chọn dòng.
const BATCH_MODEL_BY_QUERY_KEY = {
  postList: 'Post',
  albumList: 'Album',
  imageList: 'Image',
  contentVideoList: 'ContentVideo',
  fileList: 'File',
  idiomList: 'Idiom',
  cardList: 'Card',
  heroscopList: 'Heroscop',
  horoscopList: 'Horoscop',
  contentListList: 'ContentList',
  eventList: 'Event',
  userEventList: 'Event',
};

const CrudPage = ({
  title,
  subTitle,
  queryKey, // e.g. "roles"
  service, // { getList, create, update, delete }
  columns, // Base columns (actions will be added)
  // Mảng field cho RenderForm, hoặc hàm ({ isEditMode, record }) => mảng field
  // khi form cần ẩn/khoá trường tuỳ theo đang Thêm mới hay Cập nhật.
  formFields: formFieldsProp,
  formConfig = EMPTY_OBJECT, // Extra form props (width, etc)
  searchPlaceholder = 'Tìm kiếm...',
  searchFields = EMPTY_ARRAY, // Array of {label, value} for search select
  filterFields = EMPTY_ARRAY, // Array of {label, value, options} for additional selects
  addButtonLabel = 'Thêm mới',
  modalTitle = 'QUẢN LÝ',
  deleteTitle = 'Xóa mục này?',
  deleteDesc,
  itemsName = 'Mục',
  disableAdd = false,
  disableEdit = false,
  disableDelete = false,
  disableCopy = false,
  customActions, // Function (record) => ReactNode for extra table actions
  DrawerComponent, // Optional drawer for details
  onViewDetail, // Callback when view detail is clicked
  renderHeaderActions, // Optional extra header buttons (node, render cạnh nút Thêm mới)
  PreviewComponent, // Component to render preview data
  mapDataToForm,
  mapFormToData,
}) => {
  const { message, notification } = App.useApp();
  const [searchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // States to sync with URL
  const [searchText, setSearchText] = useState(searchParams.get('search') || '');
  const [searchField, setSearchField] = useState(searchParams.get('searchField') || '');
  const [filters, setFilters] = useState(() => {
    const initialFilters = {};
    filterFields.forEach((f) => {
      const val = searchParams.get(f.value);
      if (val) initialFilters[f.value] = val;
    });
    return initialFilters;
  });

  const [isRefetch, setIsRefetch] = useState(0);
  const [form] = Form.useForm();

  const formFields = useMemo(
    () =>
      typeof formFieldsProp === 'function'
        ? formFieldsProp({ isEditMode, record: selectedItem })
        : formFieldsProp,
    [formFieldsProp, isEditMode, selectedItem]
  );

  // Handle cho customActions gọi để nạp lại bảng sau khi thao tác xong.
  // Các trang đã khai báo `(record, actionRef) => ...` và gọi
  // `actionRef.current.reload()`, nhưng trước đây CrudPage chỉ truyền 1 tham số
  // nên nút Copy chạy được mà bảng không bao giờ refresh.
  // useRef để identity ổn định, không làm hỏng useMemo của tableColumns.
  const actionRef = useRef({ reload: () => setIsRefetch((prev) => prev + 1) });

  // Sync state when URL params change (e.g. back button)
  useEffect(() => {
    setSearchText(searchParams.get('search') || '');
    setSearchField(searchParams.get('searchField') || '');
    const updatedFilters = {};
    filterFields.forEach((f) => {
      const val = searchParams.get(f.value);
      if (val !== null) updatedFilters[f.value] = val;
    });
    setFilters(updatedFilters);
  }, [searchParams, filterFields]);

  // Check if columns have status field
  const hasStatusColumn = useMemo(() => {
    return columns.some((col) => col.dataIndex === 'status' || col.key === 'status');
  }, [columns]);

  const backendModelName = useMemo(() => {
    const key = Array.isArray(queryKey) ? queryKey[0] : queryKey;
    if (!key) return '';
    return BATCH_MODEL_BY_QUERY_KEY[key] || '';
  }, [queryKey]);

  // Ô tích chọn dòng chỉ có nghĩa khi còn ít nhất một thao tác hàng loạt chạy được:
  // backend phải biết model, và trang phải cho xoá hoặc có cột trạng thái để duyệt.
  // Trang chỉ-đọc như Quản lý version API trước đây vẫn hiện ô tích rồi mời bấm "Xóa nhiều".
  // Trang không dùng batch-action-content có thể tự cấp `service.batchDelete(ids)` — khi đó
  // chỉ có "Xóa nhiều", không có Duyệt/Đợi duyệt (hai nút đó chỉ chạy qua batch-action-content).
  const serviceBatchDelete = typeof service?.batchDelete === 'function' ? service.batchDelete : null;
  const enableRowSelection = useMemo(
    () =>
      (Boolean(backendModelName) && (!disableDelete || hasStatusColumn)) ||
      (Boolean(serviceBatchDelete) && !disableDelete),
    [backendModelName, disableDelete, hasStatusColumn, serviceBatchDelete]
  );

  // Reset selected keys when refetch or filter changes
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [searchText, filters, isRefetch]);

  const handleBatchAction = async (type) => {
    let confirmTitle = '';
    let confirmContent = '';
    if (type === 'approve') {
      confirmTitle = 'Duyệt nhiều mục đã chọn?';
      confirmContent = `Bạn có chắc chắn muốn duyệt ${selectedRowKeys.length} mục này?`;
    } else if (type === 'pending') {
      confirmTitle = 'Chuyển sang đợi duyệt?';
      confirmContent = `Bạn có chắc chắn muốn chuyển ${selectedRowKeys.length} mục sang trạng thái đợi duyệt?`;
    } else {
      confirmTitle = 'Xóa nhiều mục đã chọn?';
      confirmContent = `Hành động này không thể hoàn tác. Bạn có chắc chắn muốn xóa ${selectedRowKeys.length} mục này?`;
    }

    Modal.confirm({
      title: confirmTitle,
      content: confirmContent,
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      okType: type === 'delete' ? 'danger' : 'primary',
      centered: true,
      onOk: async () => {
        try {
          const res =
            type === 'delete' && !backendModelName && serviceBatchDelete
              ? await serviceBatchDelete(selectedRowKeys)
              : (
                  await request({
                    url: '/miniapp/batch-action-content',
                    method: 'POST',
                    data: {
                      ids: selectedRowKeys,
                      type,
                      model: backendModelName,
                    },
                  })
                ).data;
          if (res?.code === 200 || res?.status === 1) {
            notification.success({
              message: 'Thành công',
              description: 'Thao tác hàng loạt thành công',
            });
            setSelectedRowKeys([]);
            setIsRefetch((p) => p + 1);
          } else {
            notification.error({
              message: 'Thất bại',
              description: res?.message || 'Có lỗi xảy ra',
            });
          }
        } catch (error) {
          notification.error({
            message: 'Lỗi hệ thống',
            description: error.message,
          });
        }
      },
    });
  };

  // --- Mutations ---
  // API dùng responseException với code 200 nên lỗi validate vẫn về HTTP 200,
  // chỉ phân biệt qua status/isSuccess. Không chặn ở đây thì mutation coi là thành công,
  // hiện toast "Thành công" và đóng modal trong khi DB không hề được ghi.
  const assertApiSuccess = (res) => {
    if (res && typeof res === 'object' && (res.isSuccess === false || res.status === 0)) {
      throw new Error(res.message || res.debugMessage || 'Máy chủ từ chối yêu cầu');
    }
    return res;
  };

  const createMutation = useMutation({
    mutationFn: async (payload) => assertApiSuccess(await service.create(payload)),
    onSuccess: (res) => {
      notification.success({
        message: 'Thành công',
        // API có thể tạo nhiều bản ghi trong 1 lần (vd voucher sinh mã theo "Số lượng mã") —
        // khi đó nó trả về data.created và message nói rõ số lượng, ưu tiên hiện message đó.
        description: res?.data?.created
          ? res.message
          : `Tạo ${itemsName.toLowerCase()} thành công`,
      });
      clearDraft();
      handleCloseModal();
      setIsRefetch((p) => p + 1);
    },
    onError: (error) =>
      notification.error({
        message: 'Lỗi tạo mới',
        description: error.message,
      }),
  });

  const updateMutation = useMutation({
    mutationFn: async (payload) => assertApiSuccess(await service.update(selectedItem.id, payload)),
    onSuccess: () => {
      notification.success({
        message: 'Thành công',
        description: `Cập nhật ${itemsName.toLowerCase()} thành công`,
      });
      handleCloseModal();
      setIsRefetch((p) => p + 1);
    },
    onError: (error) =>
      notification.error({
        message: 'Lỗi cập nhật',
        description: error.message,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => assertApiSuccess(await service.delete(id)),
    onSuccess: () => {
      notification.success({
        message: 'Thành công',
        description: `Xóa ${itemsName.toLowerCase()} thành công`,
      });
      setIsRefetch((p) => p + 1);
    },
    onError: (error) =>
      notification.error({
        message: 'Lỗi xóa',
        description: error.message,
      }),
  });

  // --- Handlers ---
  // --- Draft Logic ---
  const draftKey = useMemo(() => `crud_draft_${queryKey}`, [queryKey]);

  const handleValuesChange = (_, allValues) => {
    if (!isEditMode) {
      // Clean values for draft to avoid saving non-serializable File objects
      const cleanValues = { ...allValues };
      formFields.forEach((field) => {
        if ((field.type === 'upload' || field.type === 'upload-cdn') && Array.isArray(cleanValues[field.name])) {
          cleanValues[field.name] = cleanValues[field.name]
            .map((file) => {
              if (file.url) {
                return { uid: file.uid, name: file.name, status: file.status, url: file.url, thumbUrl: file.thumbUrl };
              }
              return null;
            })
            .filter(Boolean);
        }
      });
      localStorage.setItem(draftKey, JSON.stringify(cleanValues));
    }
  };

  const clearDraft = useCallback(() => {
    localStorage.removeItem(draftKey);
  }, [draftKey]);

  const handleOpenCreate = useCallback(() => {
    setIsEditMode(false);
    setSelectedItem(null);
    form.resetFields();

    // `formFields` trong closure vẫn là bộ field của lần mở trước (có thể là bộ của form sửa)
    // nên phải tự dựng lại bộ field cho chế độ thêm mới, không thì defaultValue bị áp sai bộ.
    const createFields =
      typeof formFieldsProp === 'function' ? formFieldsProp({ isEditMode: false, record: null }) : formFieldsProp;

    // Try to restore from draft
    const savedDraft = localStorage.getItem(draftKey);
    if (savedDraft) {
      try {
        const draftData = JSON.parse(savedDraft);
        const restoredValues = {};

        createFields.forEach((field) => {
          const val = draftData[field.name];
          if (val !== undefined && val !== null) {
            if (field.type === 'date' || field.type === 'time') {
              restoredValues[field.name] = dayjs(val);
            } else if (field.type === 'range-picker' && Array.isArray(val)) {
              restoredValues[field.name] = val.map((v) => dayjs(v));
            } else {
              restoredValues[field.name] = val;
            }
          } else if (field.defaultValue !== undefined) {
            restoredValues[field.name] = field.defaultValue;
          } else if (field.defaultChecked !== undefined) {
            restoredValues[field.name] = field.defaultChecked;
          }
        });

        // Draft lưu toàn bộ giá trị form, kể cả field đăng ký động bên trong block type 'html'
        // (không có name trong formFields) — khôi phục nốt các key đó để không mất dữ liệu nháp
        const knownNames = new Set(createFields.map((field) => field.name).filter(Boolean));
        Object.keys(draftData).forEach((key) => {
          if (!knownNames.has(key) && draftData[key] !== undefined && draftData[key] !== null) {
            restoredValues[key] = draftData[key];
          }
        });

        form.setFieldsValue(restoredValues);
        notification.info({
          message: 'Khôi phục bản nháp',
          description: 'Dữ liệu trước đó bạn chưa lưu đã được tự động điền vào biểu mẫu.',
        });
      } catch (e) {
        console.error('Error restoring draft:', e);
      }
    } else {
      // Set defaults if no draft
      createFields.forEach((field) => {
        if (field.defaultValue !== undefined) {
          form.setFieldValue(field.name, field.defaultValue);
        } else if (field.defaultChecked !== undefined) {
          form.setFieldValue(field.name, field.defaultChecked);
        }
      });
    }

    setIsModalOpen(true);
  }, [form, formFieldsProp, draftKey]);

  // Hàm chung: Trích xuất giá trị từ record theo formFields
  const extractFormValues = useCallback(
    (record) => {
      const formValues = {};
      formFields.forEach((field) => {
        const val = record[field.name];
        if (val !== undefined && val !== null) {
          if (field.type === 'date' || field.type === 'time') {
            formValues[field.name] = dayjs(val);
          } else if (field.type === 'range-picker' && Array.isArray(val)) {
            formValues[field.name] = val.map((v) => dayjs(v));
          } else if ((field.type === 'upload' || field.type === 'upload-cdn') && typeof val === 'string') {
            formValues[field.name] = [
              {
                uid: '-1',
                name: val.split('/').pop() || 'image.png',
                status: 'done',
                url: val,
                thumbUrl: val,
              },
            ];
          } else {
            formValues[field.name] = val;
          }
        }
      });
      return formValues;
    },
    [formFields]
  );

  const handleOpenEdit = useCallback(
    (record) => {
      setIsEditMode(true);
      setSelectedItem(record);
      const formValues = extractFormValues(record);
      let finalData = { ...record, ...formValues };
      if (mapDataToForm) {
        finalData = { ...finalData, ...mapDataToForm(record) };
      }
      form.setFieldsValue(finalData);
      setIsModalOpen(true);
    },
    [form, extractFormValues, mapDataToForm]
  );

  const handleOpenCopy = useCallback(
    (record) => {
      setIsEditMode(false);
      setSelectedItem(null);
      const formValues = extractFormValues(record);

      // Loại bỏ ID và các trường hệ thống
      const { id, create_time, update_time, ...rest } = record;
      if (formValues.id) delete formValues.id;

      let finalData = { ...rest, ...formValues, id: undefined };
      if (mapDataToForm) {
        finalData = { ...finalData, ...mapDataToForm(record) };
      }
      form.setFieldsValue(finalData);

      setIsModalOpen(true);
      notification.success({
        message: 'Sao chép thành công',
        description: 'Dữ liệu đã được nhân bản. Vui lòng kiểm tra và lưu lại.',
      });
    },
    [form, extractFormValues, mapDataToForm]
  );

  const handleCloseModal = () => {
    setIsModalOpen(false);
    form.resetFields();
    setSelectedItem(null);
  };

  const handleSubmit = async (values) => {
    // Convert dayjs objects back to strings before submitting
    let payload = { ...values };

    for (const field of formFields) {
      // Ô dán link (field phụ `<name>__link`) ưu tiên hơn file vừa chọn — giống lichviet_cms:
      // có link thì lấy link, bỏ qua file; không có link mới xét tới file upload bên dưới.
      if (field.enterLink && typeof field.name === 'string') {
        const linkKey = uploadLinkFieldName(field.name);
        const link = typeof payload[linkKey] === 'string' ? payload[linkKey].trim() : '';
        delete payload[linkKey]; // key chỉ dùng ở FE, API không nhận
        if (link) {
          payload[field.name] = link;
          continue;
        }
      }

      const val = payload[field.name];

      if (val && dayjs.isDayjs(val)) {
        if (field.type === 'date')
          payload[field.name] = field.submitFormat ? val.format(field.submitFormat) : val.toISOString();
        if (field.type === 'time') payload[field.name] = val.format('HH:mm:ss');
      } else if (field.type === 'range-picker' && Array.isArray(val)) {
        payload[field.name] = val.map((v) => (dayjs.isDayjs(v) ? v.toISOString() : v));
      } else if (
        (field.type === 'upload-cdn' || (field.type === 'upload' && field.uploadType === 'cdn')) &&
        Array.isArray(val)
      ) {
        const fileItem = val[0];
        if (fileItem?.originFileObj && (fileItem.originFileObj instanceof File || fileItem.originFileObj instanceof Blob)) {
          try {
            const url = await uploadImageCDN(fileItem.originFileObj, () => {}, field.uploadFolder || 'lichviet');
            if (url) {
              payload[field.name] = url;
            } else {
              message.error(`Không thể tải lên ảnh: ${field.label || 'Image'}`);
              return;
            }
          } catch (error) {
            console.error(error);
            message.error('Lỗi khi tải ảnh lên CDN');
            return;
          }
        } else if (fileItem?.url) {
          payload[field.name] = fileItem.url;
        } else {
          payload[field.name] = null;
        }
      } else if (field.type === 'upload' && Array.isArray(val)) {
        // Upload file to server first, then use returned URL
        const file = val[0];
        if (file?.originFileObj && (file.originFileObj instanceof File || file.originFileObj instanceof Blob)) {
          // New file selected – upload it and get back a URL
          try {
            const url = await uploadImageToServer(file.originFileObj);
            payload[field.name] = url;
          } catch (uploadErr) {
            message.error(`Không thể tải ảnh lên: ${uploadErr.message}`);
            return;
          }
        } else if (file?.url) {
          // Existing file already uploaded – keep its URL
          payload[field.name] = file.url;
        } else {
          payload[field.name] = null;
        }
      }
    }

    if (mapFormToData) {
      try {
        const transformed = await mapFormToData(payload);
        payload = { ...payload, ...transformed };
      } catch (e) {
        console.error('Error mapping form to data:', e);
        message.error('Lỗi xử lý dữ liệu trước khi gửi: ' + e.message);
        return;
      }
    }

    if (isEditMode) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  // --- Columns Construction ---
  const tableColumns = useMemo(() => {
    const defaultActionColumn = {
      title: 'Thao tác',
      key: 'action',
      width: 150,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <TableAction
          record={record}
          onEdit={disableEdit ? null : handleOpenEdit}
          onCopy={disableCopy ? null : handleOpenCopy}
          onDelete={disableDelete ? null : (r) => deleteMutation.mutateAsync(r.id)}
          deleteTitle={deleteTitle}
          deleteDesc={deleteDesc}
          extraActions={customActions ? customActions(record, actionRef) : null}
        />
      ),
    };
    return [...columns, defaultActionColumn];
  }, [
    columns,
    disableEdit,
    disableCopy,
    disableDelete,
    customActions,
    actionRef,
    deleteTitle,
    deleteDesc,
    handleOpenEdit,
    handleOpenCopy,
    deleteMutation,
  ]);

  const whereCondition = useMemo(() => {
    const condition = { ...filters };
    // Filter ngày hiển thị/lưu URL theo `format` (DD/MM/YYYY); API nào cần dạng khác
    // (vd so `=` với cột DATE như CMS cũ) thì khai báo `submitFormat` trên filterField.
    filterFields.forEach((f) => {
      if (f.type === 'date' && f.submitFormat && condition[f.value]) {
        const d = dayjs(condition[f.value], f.format || 'DD/MM/YYYY', true);
        if (d.isValid()) condition[f.value] = d.format(f.submitFormat);
      }
    });
    if (searchText) {
      // searchField đọc từ URL nên có thể còn sót giá trị của lần cấu hình trước.
      // Trang không khai báo searchFields thì không có UI nào thể hiện lựa chọn đó
      // -> bỏ qua, tránh lọc ngầm theo field người dùng không nhìn thấy.
      if (searchField && searchFields.length > 0) {
        condition[searchField] = searchText;
      } else {
        condition.search = searchText;
      }
    }
    return condition;
  }, [searchText, searchField, searchFields, filters, filterFields]);

  return (
    <div className="max-w-[1600px] mx-auto p-6">
      <PageHeader
        title={title}
        subtitle={subTitle}
        onAdd={handleOpenCreate}
        addButtonLabel={addButtonLabel}
        searchValue={searchText}
        setSearchValue={setSearchText}
        searchFields={searchFields}
        searchField={searchField}
        setSearchField={setSearchField}
        filterFields={filterFields}
        filters={filters}
        setFilters={setFilters}
        searchPlaceholder={searchPlaceholder}
        showAddButton={!disableAdd}
        extraActions={renderHeaderActions}
      />

      {enableRowSelection && selectedRowKeys.length > 0 && (
        <div className="flex items-center justify-between p-4 mb-4 bg-blue-50 border border-blue-100 rounded-xl animate-fade-in">
          <Space>
            <span className="text-blue-800 font-medium">
              Đã chọn <strong>{selectedRowKeys.length}</strong> mục
            </span>
          </Space>
          <Space>
            {hasStatusColumn && backendModelName && (
              <>
                <Button
                  type="primary"
                  onClick={() => handleBatchAction('approve')}
                  className="bg-green-600 hover:bg-green-700 border-none rounded-lg"
                >
                  Duyệt nhiều
                </Button>
                <Button 
                  onClick={() => handleBatchAction('pending')}
                  className="text-orange-600 border-orange-200 hover:text-orange-700 rounded-lg"
                >
                  Đợi duyệt
                </Button>
              </>
            )}
            {!disableDelete && (
              <Button 
                danger 
                type="primary"
                onClick={() => handleBatchAction('delete')}
                className="rounded-lg"
              >
                Xóa nhiều
              </Button>
            )}
            <Button onClick={() => setSelectedRowKeys([])} className="rounded-lg">
              Hủy
            </Button>
          </Space>
        </div>
      )}

      <Card
        variant="borderless"
        className="shadow-sm rounded-2xl overflow-hidden border border-gray-100 bg-white"
        styles={{ body: { padding: 0 } }}
      >
        <DataTable
          queryKey={queryKey}
          apiCallBack={service.getList}
          columns={tableColumns}
          whereCondition={whereCondition}
          isRefetch={isRefetch}
          propsInput={{ 
            rowKey: 'id',
            ...(enableRowSelection
              ? {
                  rowSelection: {
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys),
                  },
                }
              : {}),
          }}
          onRowClick={disableEdit ? null : handleOpenEdit}
        />
      </Card>

      <Modal
        title={
          <Space>
            <span className="uppercase tracking-widest font-bold text-gray-800">
              {isEditMode ? `CẬP NHẬT ${itemsName.toUpperCase()}` : `THÊM MỚI ${itemsName.toUpperCase()}`}
            </span>
          </Space>
        }
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={formConfig.width || 800}
        centered
        destroyOnClose={true}
        className="premium-modal"
      >
        <div className="mt-6">
          <RenderForm
            form={form}
            fields={formFields}
            onFinish={handleSubmit}
            onCancel={handleCloseModal}
            onValuesChange={handleValuesChange}
            loadingSubmit={createMutation.isPending || updateMutation.isPending}
            extraButtons={
              PreviewComponent && (
                <Button size="large" className="w-32 rounded-xl" onClick={() => setPreviewData(form.getFieldsValue())}>
                  Xem trước
                </Button>
              )
            }
          />
        </div>
      </Modal>

      {PreviewComponent && (
        <Modal
          title="Xem trước nội dung"
          open={!!previewData}
          onCancel={() => setPreviewData(null)}
          footer={null}
          width={1000}
          centered
          className="preview-modal"
          styles={{ body: { padding: '24px', maxHeight: '80vh', overflowY: 'auto' } }}
        >
          {previewData && <PreviewComponent data={previewData} />}
        </Modal>
      )}

      {DrawerComponent && selectedItem && (
        <DrawerComponent open={!!selectedItem} onClose={() => setSelectedItem(null)} data={selectedItem} />
      )}
    </div>
  );
};

CrudPage.propTypes = {
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string,
  queryKey: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  service: PropTypes.shape({
    getList: PropTypes.func.isRequired,
    create: PropTypes.func,
    update: PropTypes.func,
    delete: PropTypes.func,
  }).isRequired,
  columns: PropTypes.array.isRequired,
  formFields: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  formConfig: PropTypes.object,
  searchPlaceholder: PropTypes.string,
  searchFields: PropTypes.array,
  filterFields: PropTypes.array,
  addButtonLabel: PropTypes.string,
  modalTitle: PropTypes.string,
  deleteTitle: PropTypes.string,
  deleteDesc: PropTypes.string,
  itemsName: PropTypes.string,
  disableAdd: PropTypes.bool,
  disableEdit: PropTypes.bool,
  disableDelete: PropTypes.bool,
  disableCopy: PropTypes.bool,
  customActions: PropTypes.func,
  DrawerComponent: PropTypes.elementType,
  onViewDetail: PropTypes.func,
  onPreview: PropTypes.func,
  renderHeaderActions: PropTypes.node,
};

export default CrudPage;
